import { PDFDocument, degrees } from 'pdf-lib';
import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import readPage from '../services/document-edit/readPage';
import { downloadData } from '../services/download';
import { toast } from 'react-hot-toast';
import { catchApiErrorMessage } from '../services/api';

interface Props {
    numPages: number,
    exportDocument: (type?: 'base64'|'buffer') => Promise<string|Uint8Array>,
    downloadProject: () => Promise<void>,
    addPageBy: (by: 'file'|'scanner', position: number) => Promise<void>,
    pdfDoc: PDFDocument | null | undefined,
    output: string|null,
    deletePages: (indices: number[]) => Promise<string | undefined>,
    updating: boolean,
    add: (data: any, position: number) => Promise<void>,
    clear: () => void,
    rotatePages: (indices: number[], rotation: number) => void,
    setOutput: (output: string|null) => void
}

const DocumentContext = React.createContext<Props>({} as Props)
const PAGE_LIMIT = Number(process.env.REACT_APP_PAGES_LIMIT)

export const DocumentContextProvider: React.FC<any> = (props) => {

    const [numPages, setNumPages] = useState<number>(0)
    const [pdfDoc, setPdfDoc] = useState<PDFDocument | null | undefined>()
    const [output, setOutputState] = useState<string|null>(null)
    const [updating, setUpdating] = useState<boolean>(false)
    const [pdfVersion, bumpPdfVersion] = useReducer((x: number) => x + 1, 0)

    const startUpdate = () => setUpdating(true)
    const endUpdate = () => setUpdating(false)

    const setOutput = useCallback((next: string|null) => {
        setOutputState(prev => {
            if (prev && prev !== next) URL.revokeObjectURL(prev)
            return next
        })
    }, [])

    useEffect(() => {
        if (pdfDoc) {
            pdfDoc.save().then(bytes => {
                const blob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' })
                setOutput(URL.createObjectURL(blob))
                setNumPages(pdfDoc.getPageCount())
                endUpdate()
            })
        } else {
            setOutput(null)
            setNumPages(0)
        }
    }, [pdfDoc, pdfVersion, setOutput])

    const outputRef = useRef<string|null>(null)
    useEffect(() => { outputRef.current = output }, [output])
    useEffect(() => () => {
        if (outputRef.current) URL.revokeObjectURL(outputRef.current)
    }, [])

    const exportDocument = async (type: 'base64'|'buffer' = 'base64') => {
        if (!pdfDoc) {
            throw Error('O PDF não está carregado.')
        }
        const output = type === 'base64' ? pdfDoc.saveAsBase64({dataUri: true}) : pdfDoc.save()
        return output
    }

    const clear = () => {
        startUpdate()
        setPdfDoc(undefined)
        setNumPages(0)
    }

    const downloadProject = async () => {
        if (!pdfDoc) return
        const bytes = await pdfDoc.save()
        downloadData(bytes, 'rascunho.pdf')
    }

    const addPageBy = async (by: 'file'|'scanner', position: number = 0) => {
        const data: any = await readPage[by](position)
        if (data) add(data, position)
    }

    const add = async (data: any, position: number = 0) => {
        data = Array.isArray(data) ? data : [data]
        if (!data || data.length === 0) {
            toast.error('Você não selecionou arquivos.')
            return
        }

        const isNew = !pdfDoc
        const basePdf = pdfDoc ?? await PDFDocument.create()

        if (basePdf.getPageCount() + data.length >= PAGE_LIMIT) {
            toast.error(`Você não pode importar mais que ${PAGE_LIMIT} páginas.`)
            return
        }

        startUpdate()

        try {
            let insertAt = Math.min(position, basePdf.getPageCount())

            for (const d of data) {
                const isPdf = d.file.type === 'application/pdf' || d.file.name.includes('.ged-project')

                if (isPdf) {
                    const importPdf = await PDFDocument.load(d.data)
                    const indices = importPdf.getPageIndices()
                    if (basePdf.getPageCount() + indices.length >= PAGE_LIMIT) {
                        toast.error(`O seu arquivo não foi importado pois ultrapassa o limite de ${PAGE_LIMIT} páginas.`)
                        endUpdate()
                        return
                    }
                    const importedPages = await basePdf.copyPages(importPdf, indices)
                    importedPages.forEach((page, i) => {
                        basePdf.insertPage(insertAt + i, page)
                    })
                    insertAt += importedPages.length
                } else if (d.file.type.includes('image')) {
                    const image = await basePdf[d.file.type.includes('image/jp') ? 'embedJpg' : 'embedPng'](d.data)
                    const { width, height } = image.scale(1)
                    const page = basePdf.insertPage(insertAt, [width, height])
                    page.drawImage(image, { x: 0, y: 0, width, height })
                    insertAt += 1
                }
            }

            if (isNew) {
                setPdfDoc(basePdf)
            } else {
                bumpPdfVersion()
            }
        } catch (e: any) {
            toast.error(catchApiErrorMessage(e))
            endUpdate()
        }
    }

    const deletePages = async (indices: number[]) => {
        if (!output || !pdfDoc) return toast.error('Aguarde o PDF iniciar.')
        if (numPages === indices.length) {
            return toast.error('Você não pode deletar todas páginas de um documento.')
        }
        startUpdate()
        const sorted = [...indices].sort((a, b) => b - a)
        sorted.forEach(i => pdfDoc.removePage(i))
        bumpPdfVersion()
    }

    const rotatePages = async (indices: number[], rotation: number) => {
        if (!pdfDoc) return toast.error('Aguarde o PDF iniciar.')
        startUpdate()
        indices.forEach(i => {
            const page = pdfDoc.getPage(i)
            page.setRotation(degrees(page.getRotation().angle + rotation))
        })
        bumpPdfVersion()
    }

    // const importFromDocument = async (id: number|string) => {
    //     startUpdate()
    //     const { data } = await api.get(`/documents/${id}/file`, {responseType: 'arraybuffer'})
    //     const i = Buffer.from(data, 'binary')
    //     // const pdfDoc = await PDFDocument.load(i)
    // } 

    return <DocumentContext.Provider value={{ clear, exportDocument, pdfDoc, downloadProject, addPageBy, output, numPages, deletePages, updating, add, rotatePages, setOutput }}>
        {props.children}
    </DocumentContext.Provider>
}

export const useDocument = () => React.useContext(DocumentContext)